import React, { useEffect, useRef } from "react";
import "./GrowSection.css";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import grassImg from "../../assets/grass.png";

gsap.registerPlugin(ScrollTrigger);

const MOBILE_BREAKPOINT = 768;

const GrowSection = () => {
  const wrapperRef      = useRef(null);
  const stickyRef       = useRef(null);
  const canvasRef       = useRef(null);
  const ctaRef          = useRef(null);
  const offscreenRef    = useRef(null);
  const layoutRef       = useRef({});
  const smoothRef       = useRef(0);

  // ─── Layout engine ────────────────────────────────────────────────────────
  const computeLayout = (totalW, totalH) => {
    const isMobile = totalW < 768;

    const panelX = isMobile ? 0             : totalW * 0.50;
    const panelW = isMobile ? totalW        : totalW * 0.50;
    const panelY = isMobile ? totalH * 0.44 : 0;
    const panelH = isMobile ? totalH - panelY : totalH;

    const grassH = Math.max(80, panelH * 0.15);
    const grassY = totalH - grassH;
    const grassDrawY = totalH - (grassH * 0.8);

    const treeOriginX = panelX + panelW * (isMobile ? 0.50 : 0.46);
    const treeOriginY = grassY + grassH * 0.90;

    const topMargin = isMobile ? 0.06 : 0.08;
    const targetTop = panelY + panelH * topMargin;
    const treeScale = (treeOriginY - targetTop) / 0.88;

    return {
      isMobile,
      panelX, panelW, panelY, panelH,
      grassH, grassY, grassDrawY,
      treeOriginX, treeOriginY,
      treeScale,
      totalW, totalH,
    };
  };

  // ─── Branch data ──────────────────────────────────────────────────────────
  const buildBranches = (originX, originY, treeScale) => {
    const branches  = [];
    const MAX_DEPTH = 7;
    const recurse = (x, y, len, angle, depth) => {
      const x2 = x + Math.cos(angle) * len;
      const y2 = y + Math.sin(angle) * len;
      branches.push({ x1: x, y1: y, x2, y2, depth });
      if (depth >= MAX_DEPTH) return;
      const nextLen = len * 0.64;
      const spread  = 0.36 + 0.05 * depth;
      const wobble  = Math.sin(x * 0.031 + y * 0.017 + depth) * 0.12;
      recurse(x2, y2, nextLen, angle - spread + wobble,       depth + 1);
      recurse(x2, y2, nextLen, angle + spread + wobble * 0.7, depth + 1);
    };
    recurse(originX, originY, treeScale * 0.26, -Math.PI / 2 + 0.08, 0);
    return branches;
  };

  // ─── Leaf data ────────────────────────────────────────────────────────────
  const generateLeaves = (W, H, dpr, branches) => {
    const leaves = [];
    const points = [];
    for (const b of branches) {
      const weight = b.depth >= 5 ? 8 : b.depth >= 3 ? 12 : b.depth >= 1 ? 3 : 1;
      for (let w = 0; w < weight; w++) {
        points.push({ x: b.x2,               y: b.y2               });
        points.push({ x: (b.x1 + b.x2) / 2,  y: (b.y1 + b.y2) / 2 });
      }
    }
    const greens = [
      "#0a2e0a","#0d3d0d","#112e11","#144d14","#1a5c1a",
      "#1e6b1e","#236b23","#2d7a2d","#2d8c2d","#338c33",
      "#3a9c3a","#3aac3a","#42a142","#4caf50","#55a630",
      "#4a7c2f","#3d6b28","#1f5c1f","#286028","#346b1a",
    ];
    const spread = (W / dpr) * 0.013;
    for (let i = 0; i < 120000; i++) {
      const pt = points[Math.floor(Math.random() * points.length)];
      const u1 = Math.max(1e-10, Math.random());
      const u2 = Math.random();
      const gx = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
      const gy = Math.sqrt(-2 * Math.log(u1)) * Math.sin(2 * Math.PI * u2);
      const lx = pt.x + gx * spread;
      const ly = pt.y + gy * spread * 0.82;
      const t    = Math.random();
      const size = t < 0.55
        ? (0.9 + Math.random() * 2.0) * dpr
        : t < 0.85
        ? (2.0 + Math.random() * 2.8) * dpr
        : (3.2 + Math.random() * 4.0) * dpr;
      leaves.push({
        lx, ly, size,
        aspect:  0.28 + Math.random() * 0.50,
        angle:   Math.random() * Math.PI * 2,
        col:     greens[Math.floor(Math.random() * greens.length)],
        opacity: 0.55 + Math.random() * 0.45,
      });
    }
    leaves.sort((a, b) => a.ly - b.ly);
    return leaves;
  };

  // ─── Offscreen canopy ─────────────────────────────────────────────────────
  const prerenderCanopy = (W, H, leaves, originX, originY, treeScale, dpr) => {
    if (!W || !H || W <= 0 || H <= 0) return null;

    const off = document.createElement("canvas");
    off.width  = Math.max(1, Math.round(W));
    off.height = Math.max(1, Math.round(H));
    const oc = off.getContext("2d");
    for (const { lx, ly, size, angle, aspect, col, opacity } of leaves) {
      oc.save();
      oc.globalAlpha = opacity;
      oc.translate(lx * dpr, ly * dpr);
      oc.rotate(angle);
      oc.beginPath();
      oc.ellipse(0, 0, size * aspect, size, 0, 0, Math.PI * 2);
      oc.fillStyle = col;
      oc.fill();
      oc.restore();
    }
    const cx = originX * dpr;
    const cy = (originY - treeScale * 0.52) * dpr;
    const r  = W * 0.30;
    const grad = oc.createRadialGradient(cx, cy, r * 0.78, cx, cy, r);
    grad.addColorStop(0, "rgba(0,0,0,0)");
    grad.addColorStop(1, "rgba(0,0,0,1)");
    oc.globalCompositeOperation = "destination-out";
    oc.fillStyle = grad;
    oc.fillRect(0, 0, off.width, off.height);
    oc.globalCompositeOperation = "source-over";
    return off;
  };

  // ─── Branch drawing ───────────────────────────────────────────────────────
  const drawRealisticBranch = (ctx, x1, y1, x2, y2, thickness, depth, dpr) => {
    if (depth <= 1) {
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2);
      ctx.strokeStyle = depth === 0 ? "#1e0d02" : "#2a1206";
      ctx.lineWidth   = thickness * 1.06;
      ctx.lineCap     = "round"; ctx.stroke();

      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2);
      ctx.strokeStyle = depth === 0 ? "#5a3510" : "#6b4015";
      ctx.lineWidth   = thickness * 0.88;
      ctx.lineCap     = "round"; ctx.stroke();

      const nx = -(y2 - y1), ny = (x2 - x1);
      const nl = Math.sqrt(nx * nx + ny * ny) || 1;
      const ox = (nx / nl) * thickness * 0.18;
      const oy = (ny / nl) * thickness * 0.18;

      ctx.beginPath();
      ctx.moveTo(x1 - ox, y1 - oy); ctx.lineTo(x2 - ox, y2 - oy);
      ctx.strokeStyle = "rgba(150,95,42,0.25)";
      ctx.lineWidth   = thickness * 0.28; ctx.lineCap = "round"; ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(x1 + ox, y1 + oy); ctx.lineTo(x2 + ox, y2 + oy);
      ctx.strokeStyle = "rgba(10,4,0,0.30)";
      ctx.lineWidth   = thickness * 0.20; ctx.lineCap = "round"; ctx.stroke();

      if (depth === 0) drawBarkDetails(ctx, x1, y1, x2, y2, thickness, dpr);
    } else {
      const t = Math.min(depth / 7, 1);
      const r = Math.round(78  + t * 55);
      const g = Math.round(44  + t * 32);
      const b = Math.round(12  + t * 14);
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2);
      ctx.strokeStyle = `rgb(${r},${g},${b})`;
      ctx.lineWidth   = thickness; ctx.lineCap = "round"; ctx.stroke();
    }
  };

  const drawBarkDetails = (ctx, x1, y1, x2, y2, thickness, dpr) => {
    const dx = x2 - x1, dy = y2 - y1;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const nx  = -dy / len, ny = dx / len;
    for (let i = 1; i <= 7; i++) {
      const t  = i / 8;
      const cx = x1 + dx * t;
      const cy = y1 + dy * t;
      const hw = thickness * 0.42 * (0.85 + Math.sin(t * 11.3) * 0.15);
      ctx.beginPath();
      ctx.moveTo(cx + nx * -hw, cy + ny * -hw);
      ctx.lineTo(cx + nx *  hw, cy + ny *  hw);
      ctx.strokeStyle = `rgba(18,7,1,${0.08 + Math.sin(t * 19) * 0.04})`;
      ctx.lineWidth   = dpr * (0.7 + Math.abs(Math.sin(t * 13)) * 0.5);
      ctx.stroke();
    }
    for (const kt of [0.20, 0.47, 0.73]) {
      const kx = x1 + dx * kt + nx * thickness * Math.sin(kt * 27) * 0.12;
      const ky = y1 + dy * kt + ny * thickness * Math.sin(kt * 27) * 0.12;
      const kr = thickness * (0.06 + Math.abs(Math.sin(kt * 83)) * 0.055);
      ctx.beginPath();
      ctx.ellipse(kx, ky, kr * 1.5, kr, Math.sin(kt * 5) * 0.4, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(12,4,0,0.55)"; ctx.fill();
      ctx.beginPath();
      ctx.ellipse(kx, ky, kr * 2.2, kr * 1.7, Math.sin(kt * 5) * 0.4, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(25,10,2,0.28)";
      ctx.lineWidth   = dpr * 1.2; ctx.stroke();
    }
  };

  // ─── Fractal tree ─────────────────────────────────────────────────────────
  const drawFractalTree = (ctx, layout, progress, dpr) => {
    const { treeOriginX, treeOriginY, treeScale } = layout;
    const MAX_DEPTH = 7;
    const recurse = (x, y, len, angle, depth) => {
      const startAt = depth * 0.09;
      const levelP  = Math.max(0, Math.min(1, (progress - startAt) / (1.0 - startAt)));
      if (levelP <= 0) return;
      const x2 = x + Math.cos(angle) * len * levelP;
      const y2 = y + Math.sin(angle) * len * levelP;
      const maxWidth  = Math.max(6, treeScale * 0.07) * dpr;
      const thickness = Math.max(0.8 * dpr, maxWidth * Math.pow(0.52, depth));
      drawRealisticBranch(ctx, x, y, x2, y2, thickness, depth, dpr);
      if (levelP < 0.15 || depth >= MAX_DEPTH) return;
      const nextLen = len * 0.64;
      const spread  = 0.36 + 0.05 * depth;
      const wobble  = Math.sin(x * 0.031 + y * 0.017 + depth) * 0.12;
      recurse(x2, y2, nextLen, angle - spread + wobble,       depth + 1);
      recurse(x2, y2, nextLen, angle + spread + wobble * 0.7, depth + 1);
    };
    recurse(treeOriginX, treeOriginY, treeScale * 0.26, -Math.PI / 2 + 0.08, 0);
  };

  // ─── Effect ───────────────────────────────────────────────────────────────
  useEffect(() => {
    // On mobile, skip the entire canvas/tree/ScrollTrigger setup — no
    // growth graphic renders, no scroll-pinning, no rAF loop running in
    // the background. Desktop (>= 768px) runs exactly as before.
    if (window.innerWidth < MOBILE_BREAKPOINT) {
      if (ctaRef.current) {
        ctaRef.current.style.opacity = 1;
        ctaRef.current.style.transform = "translateY(0px) scale(1)";
        ctaRef.current.style.pointerEvents = "auto";
      }
      return;
    }

    const wrapper = wrapperRef.current;
    const sticky  = stickyRef.current;
    const canvas  = canvasRef.current;
    const ctx     = canvas.getContext("2d");
    const ctaEl   = ctaRef.current;
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);

    const gImg = new Image();
    gImg.src   = grassImg;

    let started = false;

    const rebuild = (layout, dpr) => {
      const { treeOriginX, treeOriginY, treeScale, totalW, totalH } = layout;
      const branches = buildBranches(treeOriginX, treeOriginY, treeScale);
      const leaves   = generateLeaves(totalW * dpr, totalH * dpr, dpr, branches);
      offscreenRef.current = prerenderCanopy(
        totalW * dpr, totalH * dpr, leaves,
        treeOriginX, treeOriginY, treeScale, dpr
      );
    };

    const startTicking = () => {
      if (started) return;
      started = true;
      raf = requestAnimationFrame(tick);
      ScrollTrigger.create({
        trigger: wrapper,
        start:   "top top",
        end:     "bottom bottom",
        onUpdate: (self) => { target = self.progress; },
      });
    };

    const resize = () => {
      const dpr    = window.devicePixelRatio || 1;
      const totalW = window.innerWidth;
      const totalH = sticky.offsetHeight;

      if (totalW <= 0 || totalH <= 0) return;

      canvas.width  = totalW * dpr;
      canvas.height = totalH * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const layout = computeLayout(totalW, totalH);
      layout.dpr   = dpr;
      layoutRef.current = layout;

      rebuild(layout, dpr);
      render(smoothRef.current);
      startTicking();
    };

    const render = (prog) => {
      const L = layoutRef.current;
      if (!L.totalW || !L.totalH) return;
      const {
        totalW, totalH, dpr,
        panelX, panelW, panelY,
        grassH, grassDrawY,
      } = L;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, totalW, totalH);

      // ── Grass: tiled full viewport width ──
      if (gImg.complete && gImg.naturalWidth > 0) {
        const imgAspect = gImg.naturalWidth / gImg.naturalHeight;
        const tileW     = grassH * imgAspect;
        const stripH    = totalH - grassDrawY + (grassH * 0.6);

        if (tileW > 0 && stripH > 0) {
          let x = 0;
          while (x < totalW) {
            ctx.drawImage(gImg, x, grassDrawY, tileW, stripH);
            x += tileW;
          }
        }
      }

      // ── Tree: clipped to right panel ──
      ctx.save();
      ctx.beginPath();
      ctx.rect(panelX, panelY, panelW, totalH);
      ctx.clip();

      const tp = easeOut(Math.min(prog / 0.45, 1));
      if (tp > 0) drawFractalTree(ctx, L, tp, dpr);

      ctx.restore();

      // ── Leaves: clipped to right panel ──
      const lp = Math.max(0, Math.min(1, (prog - 0.45) / 0.20));
      const off = offscreenRef.current;
      if (lp > 0 && off && off.width > 0 && off.height > 0) {
        ctx.save();
        ctx.beginPath();
        ctx.rect(panelX, panelY, panelW, totalH);
        ctx.clip();
        ctx.globalAlpha = lp;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.drawImage(off, 0, 0);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.restore();
      }

      // ── CTA fades in at 30% scroll ──
      const cp = prog >= 0.30 ? 1 : 0;
      if (ctaEl) {
        ctaEl.style.opacity       = cp;
        ctaEl.style.transform     = cp === 1 ? "translateY(0px) scale(1)" : "translateY(20px) scale(0.95)";
        ctaEl.style.pointerEvents = cp === 1 ? "auto" : "none";
      }
    };

    let target = 0, raf;
    const tick = () => {
      smoothRef.current += (target - smoothRef.current) * 0.06;
      render(smoothRef.current);
      raf = requestAnimationFrame(tick);
    };

    const init = () => {
      resize();
    };

    gImg.onload = init;
    if (gImg.complete && gImg.naturalWidth > 0) init();

    const ro = new ResizeObserver(() => {
      clearTimeout(ro._t);
      ro._t = setTimeout(resize, 60);
    });
    ro.observe(sticky);

    const onDprChange = () => {
      clearTimeout(onDprChange._t);
      onDprChange._t = setTimeout(resize, 60);
    };
    const dprMQ = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
    dprMQ.addEventListener("change", onDprChange);
    window.addEventListener("orientationchange", () => setTimeout(resize, 150));

    return () => {
      cancelAnimationFrame(raf);
      ScrollTrigger.getAll().forEach(t => t.kill());
      ro.disconnect();
      dprMQ.removeEventListener("change", onDprChange);
      window.removeEventListener("orientationchange", resize);
    };
  }, []);

  return (
    <div className="grow-wrapper" ref={wrapperRef}>
      <div className="grow-sticky" ref={stickyRef}>
        <canvas ref={canvasRef} className="grow-canvas" />
        <div className="grow-text">
          <h2>
            WELCOME TO{" "}
            <span className="pg-text">
              <span className="green-text">PROJECT GREEN.</span>
            </span>
          </h2>
          <p>
            This intranet is your central hub, built to help every team member
            access what they need, stay informed, and move forward with
            confidence. From onboarding to daily tools, everything is here.
          </p>
          <br /><br />
          <h3
            ref={ctaRef}
            style={{
              opacity: 0,
              transform: "translateY(20px) scale(0.95)",
              transition: "opacity 0.15s ease, transform 0.15s ease",
              pointerEvents: "none",
            }}
          >
            If you are new <a href="/onboarding">click here</a> or navigate to
            our onboarding page.
          </h3>
        </div>
      </div>
    </div>
  );
};

export default GrowSection;